import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type TitleRow = {
  id: string;
  code: string;
  title: string;
  author?: string | null;
  available_count?: number;
};

type CopyRow = {
  id: string;
  accession_no: string;
  status: string;
  location_label?: string | null;
  title_name?: string;
  title_code?: string;
};

type IssueForm = {
  student_id: string;
  copy_id?: string;
  accession_no?: string;
  issued_on: string;
  loan_days?: number;
  notes?: string;
};

export default function LibraryIssuePage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canCirculate = Boolean(
    user?.permissions?.includes('education.library.circulate') ||
      user?.permissions?.includes('education.admin')
  );
  const [form] = Form.useForm<IssueForm>();
  const presetStudent = searchParams.get('student_id') || undefined;
  const [titleQ, setTitleQ] = useState('');
  const [titleId, setTitleId] = useState<string | undefined>();
  const [pickMode, setPickMode] = useState<'search' | 'accession'>('search');

  useEffect(() => {
    form.setFieldsValue({
      student_id: presetStudent,
      issued_on: new Date().toISOString().slice(0, 10),
      loan_days: 14,
    });
  }, [form, presetStudent]);

  const titles = useQuery({
    queryKey: ['education-library-titles-issue', titleQ],
    enabled: canCirculate && pickMode === 'search',
    queryFn: async () => {
      const res = await api.get<TitleRow[]>('/education/library/titles', {
        params: { q: titleQ || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const copies = useQuery({
    queryKey: ['education-library-copies-issue', titleId],
    enabled: canCirculate && pickMode === 'search' && Boolean(titleId),
    queryFn: async () => {
      const res = await api.get<CopyRow[]>('/education/library/copies', {
        params: { title_id: titleId, status: 'available' },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const titleOptions = useMemo(
    () =>
      (titles.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.title}${row.author ? ` — ${row.author}` : ''} (${row.code}) · ${t('available')}: ${Number(row.available_count ?? 0)}`,
      })),
    [t, titles.data]
  );

  const copyOptions = useMemo(
    () =>
      (copies.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.accession_no}${row.location_label ? ` · ${row.location_label}` : ''}`,
      })),
    [copies.data]
  );

  const issue = useMutation({
    mutationFn: (values: IssueForm) => {
      const payload: Record<string, unknown> = {
        student_id: values.student_id,
        issued_on: values.issued_on,
        loan_days: values.loan_days,
        notes: values.notes,
      };
      if (pickMode === 'search' && values.copy_id) {
        payload.copy_id = values.copy_id;
      } else if (values.accession_no) {
        payload.accession_no = String(values.accession_no).trim().toUpperCase();
      }
      return api.post('/education/library/loans/issue', payload);
    },
    onSuccess: () => {
      message.success(t('libraryIssued'));
      queryClient.invalidateQueries({ queryKey: ['education-library-loans'] });
      queryClient.invalidateQueries({ queryKey: ['education-library-titles'] });
      queryClient.invalidateQueries({ queryKey: ['education-library-copies'] });
      navigate('/education/library/loans');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canCirculate) {
    return (
      <EducationPageChrome title={t('libraryIssue')} description={t('libraryIssueIntro')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('libraryIssue')}
      description={t('libraryIssueIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/library/loans')}>{t('libraryLoans')}</Button>
          <Button onClick={() => navigate('/education/library')}>{t('backToLibrary')}</Button>
        </Space>
      }
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (pickMode === 'search' && !values.copy_id) {
              message.error(t('libraryPickCopyRequired'));
              return;
            }
            if (pickMode === 'accession' && !values.accession_no) {
              message.error(t('libraryPickAccessionRequired'));
              return;
            }
            issue.mutate(values);
          }}
        >
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect style={{ width: '100%' }} disabled={Boolean(presetStudent)} />
          </Form.Item>

          <Form.Item label={t('findBook')}>
            <Select
              value={pickMode}
              style={{ width: '100%' }}
              options={[
                { value: 'search', label: t('findBookByTitle') },
                { value: 'accession', label: t('findBookByAccession') },
              ]}
              onChange={(mode: 'search' | 'accession') => {
                setPickMode(mode);
                setTitleId(undefined);
                form.setFieldsValue({ copy_id: undefined, accession_no: undefined });
              }}
            />
          </Form.Item>

          {pickMode === 'search' ? (
            <>
              <Form.Item label={t('title')} required>
                <Select
                  showSearch
                  allowClear
                  placeholder={t('searchBookByName')}
                  filterOption={false}
                  value={titleId}
                  options={titleOptions}
                  loading={titles.isFetching}
                  onSearch={(value) => setTitleQ(value)}
                  onChange={(value) => {
                    setTitleId(value);
                    form.setFieldsValue({ copy_id: undefined });
                  }}
                  notFoundContent={
                    titles.isFetching ? t('libraryLoading') : titleQ ? t('noBooksFound') : t('typeToSearchBooks')
                  }
                />
              </Form.Item>
              <Form.Item
                name="copy_id"
                label={t('availableCopy')}
                rules={[{ required: true, message: t('libraryPickCopyRequired') }]}
              >
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={titleId ? t('selectAvailableCopy') : t('selectTitleFirst')}
                  disabled={!titleId}
                  options={copyOptions}
                  loading={copies.isFetching}
                  notFoundContent={
                    copies.isFetching
                      ? t('libraryLoading')
                      : titleId
                        ? t('noAvailableCopies')
                        : t('selectTitleFirst')
                  }
                />
              </Form.Item>
              {titleId && copies.data && copies.data.length === 0 ? (
                <Typography.Paragraph type="secondary">{t('noAvailableCopies')}</Typography.Paragraph>
              ) : null}
            </>
          ) : (
            <Form.Item
              name="accession_no"
              label={t('accessionNo')}
              rules={[{ required: true, message: t('libraryPickAccessionRequired') }]}
            >
              <Input placeholder={t('accessionNoHint')} />
            </Form.Item>
          )}

          <Form.Item name="issued_on" label={t('issuedOn')} rules={[{ required: true }]}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="loan_days" label={t('loanDays')}>
            <InputNumber min={1} max={90} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label={t('notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={issue.isPending}>
              {t('issueItem')}
            </Button>
            <Button onClick={() => navigate('/education/library/loans')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
